import { Logic } from '~common/ui-logic/logic'
import type { TaskState } from '~/services/cache/types'
import type { AppContextType } from '~/app-context'
import type { DateRange } from 'react-day-picker'
import { sleep } from '~common/utils/sleep'

import { debounce } from '~common/utils/debounce'
import { SortDirection } from '~common/components/sort-indicator'

export interface DatePickerLogicDependencies {
    /** Callback when the date range changes */
    onChange?: (dateRange: DateRange | Date) => void
    /** Callback when Enter is pressed in the input */
    onConfirm?: (dateRange: DateRange | Date | undefined) => void
    /** Placeholder text for the input */
    placeholder?: string
    /** Whether the component is disabled */
    disabled?: boolean
    /** Custom class name */
    className?: string
    /** Size variant */
    size?: 'small' | 'medium' | 'large'
    initValue?: DateRange | Date
    singleDate?: boolean
    disableOnBlur?: boolean
    initActive?: boolean
    onSortClick?: () => void
    sortDirection?: SortDirection
    inputValidation?: ['numbersOnly']
    icon?: string
    width?: string
    label?: string
    variant?: 'tableHeader'
    clearOnClick?: (shiftKey?: boolean) => void
    resizable?: 'horizontal' | 'vertical' | 'both'
}

export interface DatePickerLogicState {
    selectedRange: DateRange | Date | undefined
    textValue: string
    isOpen: boolean
    active: boolean
    loadState: TaskState
}

/**
 * Converts a date to UTC midnight based on its calendar day.
 * This ensures dates are timezone-agnostic - if a user selects "Jan 1, 2024",
 * it will always be represented as UTC midnight on Jan 1, 2024, regardless of
 * the user's timezone.
 *
 * For dates from react-day-picker (which are in local time), we use local getters
 * to preserve the calendar day the user sees. For dates that are already UTC,
 * we use UTC getters to avoid timezone conversion issues.
 */
function toUTCMidnight(date: Date): Date {
    // Check if the date is already at UTC midnight (within 1 second tolerance)
    const isAlreadyUTCMidnight =
        date.getUTCHours() === 0 &&
        date.getUTCMinutes() === 0 &&
        date.getUTCSeconds() === 0 &&
        date.getUTCMilliseconds() < 1000

    if (isAlreadyUTCMidnight) {
        // Date is already UTC midnight, return as-is
        return new Date(date)
    }

    // Date is likely from react-day-picker (local time) or has a time component
    // Extract calendar day components using local getters to preserve the calendar day
    // the user sees (e.g., if they select Jan 1 in their calendar, we want Jan 1 UTC)
    // This handles the case where react-day-picker creates dates in local timezone
    const year = date.getFullYear()
    const month = date.getMonth()
    const day = date.getDate()
    // Create a new UTC date at midnight for that calendar day
    return new Date(Date.UTC(year, month, day, 0, 0, 0, 0))
}

/**
 * Converts a date to UTC end-of-day (23:59:59.999) based on its calendar day.
 */
function toUTCEndOfDay(date: Date): Date {
    // Check if the date is already at UTC end-of-day
    const isAlreadyUTCEndOfDay =
        date.getUTCHours() === 23 &&
        date.getUTCMinutes() === 59 &&
        date.getUTCSeconds() === 59 &&
        date.getUTCMilliseconds() >= 999

    if (isAlreadyUTCEndOfDay) {
        // Date is already UTC end-of-day, return as-is
        return new Date(date)
    }

    // Extract calendar day components using local getters
    const year = date.getFullYear()
    const month = date.getMonth()
    const day = date.getDate()
    return new Date(Date.UTC(year, month, day, 23, 59, 59, 999))
}

/**
 * Converts a UTC date to a local date representing the same calendar day.
 * This is needed because react-day-picker interprets dates in local time.
 * If we pass a UTC date directly, it may display the wrong day due to timezone offset.
 */
function UTCToLocalForDisplay(utcDate: Date): Date {
    // Extract the UTC calendar day components
    const year = utcDate.getUTCFullYear()
    const month = utcDate.getUTCMonth()
    const day = utcDate.getUTCDate()
    // Create a local date with those same calendar day components
    // This ensures react-day-picker displays the correct day
    return new Date(year, month, day)
}

/**
 * Converts a UTC DateRange to a local DateRange for display in react-day-picker.
 */
function UTCRangeToLocalForDisplay(
    range: DateRange | undefined,
): DateRange | undefined {
    if (!range || !range.from) {
        return undefined
    }

    const localFrom = UTCToLocalForDisplay(range.from)

    if (!range.to) {
        return { from: localFrom }
    }

    const localTo = UTCToLocalForDisplay(range.to)
    return {
        from: localFrom,
        to: localTo,
    }
}

export class DatePickerLogic extends Logic<
    DatePickerLogicDependencies & AppContextType,
    DatePickerLogicState
> {
    containerRef = { current: null as HTMLDivElement | null }

    debouncedOnChange = debounce((value: DateRange | Date | undefined) => {
        if (value == null) {
            this.deps.clearOnClick?.()
        } else {
            this.deps.onChange?.(value)
        }
    }, 300)

    /**
     * Converts a UTC date to a local date for display in react-day-picker.
     * This ensures the calendar shows the correct day.
     */
    UTCToLocalForDisplay = (utcDate: Date): Date => {
        return UTCToLocalForDisplay(utcDate)
    }

    /**
     * Converts a UTC DateRange to a local DateRange for display in react-day-picker.
     */
    UTCRangeToLocalForDisplay = (
        range: DateRange | undefined,
    ): DateRange | undefined => {
        return UTCRangeToLocalForDisplay(range)
    }

    getInitialState = (): DatePickerLogicState => {
        return {
            selectedRange: this.deps.initValue,
            textValue: this.deps.initValue
                ? formatRange(this.deps.initValue)
                : '',
            isOpen: false,
            active: this.deps.initActive || true,
            loadState: 'pristine',
        }
    }

    async initialize() {
        this.setState({
            textValue: this.deps.initValue
                ? formatRange(this.deps.initValue)
                : '',
        })
    }

    shouldReload(
        oldDeps: DatePickerLogicDependencies,
        newDeps: DatePickerLogicDependencies,
    ): boolean {
        return oldDeps.initValue !== newDeps.initValue
    }

    handleConfirm = (dateRange: string) => {
        const parsed = parseDateInput(dateRange, this.deps.singleDate)
        this.setState({
            isOpen: false,
        })
        if (!parsed) {
            this.deps.onConfirm?.(undefined)
            return
        }
        this.deps.onConfirm?.(parsed)
    }

    /**
     * Converts a date range to UTC ISO format
     */
    convertToUTC = (range: DateRange | undefined): DateRange | undefined => {
        if (!range || !range.from) {
            return undefined
        }

        const fromUTC = toUTCMidnight(new Date(range.from))

        if (!range.to) {
            return { from: fromUTC }
        }

        const toUTC = toUTCEndOfDay(new Date(range.to))

        return {
            from: fromUTC,
            to: toUTC,
        }
    }

    /**
     * Normalizes a date range by setting 'from' to the start of its day and 'to' to the end of its day.
     * Assumes both from and to are provided.
     */
    normalizeRange = (range: DateRange): DateRange | undefined => {
        if (!range.from || !range.to) {
            return undefined
        }
        return {
            from: toUTCMidnight(new Date(range.from)),
            to: toUTCEndOfDay(new Date(range.to)),
        }
    }
    handleInputChange = (newValue?: string): void => {
        if (!newValue || newValue.trim() === '') {
            this.setState({
                selectedRange: undefined,
                textValue: '',
            })
            this.debouncedOnChange(undefined)
            return
        }

        const parsed = parseDateInput(newValue, this.deps.singleDate)

        this.setState({
            selectedRange: parsed,
            textValue: newValue,
        })
        this.debouncedOnChange(parsed)
    }

    handleKeyDown = (key: string, currentValue: string) => {
        if (key === 'Enter') {
            const parsed = parseDateInput(currentValue, this.deps.singleDate)
            if (!parsed) {
                this.setState({
                    selectedRange: undefined,
                    isOpen: false,
                })
                this.deps.onChange?.({ from: undefined, to: undefined })
                return
            }

            if (this.deps.singleDate && parsed instanceof Date) {
                const normalized = toUTCMidnight(parsed)
                this.setState({
                    selectedRange: normalized,
                    isOpen: false,
                })
                this.deps.onChange?.(normalized)
            } else if (
                !this.deps.singleDate &&
                'from' in parsed &&
                'to' in parsed
            ) {
                const normalized = this.normalizeRange(parsed)
                this.setState({
                    selectedRange: normalized,
                    isOpen: false,
                })
                if (normalized) {
                    this.deps.onChange?.(normalized)
                }
            }
        }
    }

    handleCalendarSelect = (range: DateRange | Date | undefined): void => {
        if (this.deps.singleDate) {
            // Single date mode
            if (range instanceof Date) {
                const normalized = toUTCMidnight(range)
                this.setState({
                    selectedRange: normalized,
                    textValue: formatRange(normalized),
                })
                this.deps.onChange?.(normalized)
            } else if (range && 'from' in range && range.from) {
                // react-day-picker might pass a DateRange with just 'from' in single mode
                const normalized = toUTCMidnight(range.from)
                this.setState({
                    selectedRange: normalized,
                    textValue: formatRange(normalized),
                })
                this.deps.onChange?.(normalized)
            } else {
                this.setState({
                    selectedRange: undefined,
                    textValue: '',
                })
                // In single date mode, we can't call onChange with undefined
                // The parent should handle the cleared state via onConfirm if needed
            }
        } else {
            // Range mode
            let toPublish: DateRange | undefined
            if (range && !(range instanceof Date) && range.to !== undefined) {
                const normalized = this.normalizeRange(range)
                if (normalized) {
                    toPublish = normalized
                    this.setState({
                        selectedRange: normalized,
                        textValue: formatRange(normalized),
                    })
                } else {
                    this.setState({
                        selectedRange: range,
                        textValue: formatRange(range),
                    })
                }
            } else {
                this.setState({
                    selectedRange: range as DateRange | undefined,
                    textValue: range ? formatRange(range as DateRange) : '',
                })
            }
            if (toPublish) {
                this.deps.onChange?.(toPublish)
            }
        }
    }

    toggleCalendar = (currentIsOpen: boolean) => {
        if (!this.deps.disabled) {
            this.setState({ isOpen: !currentIsOpen })
        }
    }

    closeCalendar = () => {
        this.setState({ isOpen: false })
    }

    setIsOpen = (open: boolean) => {
        this.setState({ isOpen: open })
    }

    handleClickOutside = (event: MouseEvent) => {
        if (
            this.containerRef.current &&
            !this.containerRef.current.contains(event.target as Node)
        ) {
            this.closeCalendar()
        }
    }

    handleClear = async (shiftKey?: boolean) => {
        this.setState({
            selectedRange: undefined,
            textValue: '',
            isOpen: false,
        })

        await sleep(100)
        if (this.deps.singleDate) {
            // In single date mode, onChange expects Date | DateRange, but clearing means no selection
            // We'll call it with undefined which matches the expected behavior
            this.deps.onConfirm?.(undefined)
        } else {
            this.deps.onChange?.({ from: undefined, to: undefined })
        }
        this.deps.clearOnClick?.(shiftKey)
    }
}

/**
 * Parses various date input formats and returns a DateRange or Date
 * Supported formats:
 * - "2022" -> Jan 1, 2022 to Dec 31, 2022 (or single date if singleDate=true)
 * - "2022-2023" -> Jan 1, 2022 to Dec 31, 2023
 * - "202210" or "2022.10" -> Oct 1, 2022 to Oct 31, 2022 (or single date if singleDate=true)
 * - "202210-202212" or "2022.10-2022.12" -> Oct 1, 2022 to Dec 31, 2022
 * - "20241005" or "2024-10-05" -> Oct 5th 2024 (single date)
 */
function parseDateInput(
    input: string,
    singleDate?: boolean,
): DateRange | Date | undefined {
    if (!input.trim()) return undefined

    // Remove any whitespace
    const cleanInput = input.trim()
    let result: DateRange | undefined = undefined

    // Check for year range (e.g., "2022-2023")
    const yearRangeMatch = cleanInput.match(/^(\d{4})-(\d{4})$/)
    if (yearRangeMatch) {
        const startYear = parseInt(yearRangeMatch[1])
        const endYear = parseInt(yearRangeMatch[2])

        if (
            startYear >= 2020 &&
            endYear >= startYear &&
            endYear <= new Date().getFullYear()
        ) {
            result = {
                from: new Date(Date.UTC(startYear, 0, 1, 0, 0, 0, 0)), // Start of year in UTC
                to: new Date(Date.UTC(endYear, 11, 31, 23, 59, 59, 999)), // End of year in UTC
            }
        }
    }

    // Check for year-month range (e.g., "202210-202212" or "2022.10-2022.12")
    const monthRangeMatch = cleanInput.match(
        /^(\d{4})[.-]?(\d{1,2})-(\d{4})[.-]?(\d{1,2})$/,
    )
    if (monthRangeMatch) {
        const startYear = parseInt(monthRangeMatch[1])
        const startMonth = parseInt(monthRangeMatch[2]) - 1 // JS months are 0-indexed
        const endYear = parseInt(monthRangeMatch[3])
        const endMonth = parseInt(monthRangeMatch[4]) - 1

        if (
            startYear >= 2020 &&
            endYear >= startYear &&
            startMonth >= 0 &&
            startMonth <= 11 &&
            endMonth >= 0 &&
            endMonth <= 11 &&
            endYear <= new Date().getFullYear()
        ) {
            const startDate = new Date(
                Date.UTC(startYear, startMonth, 1, 0, 0, 0, 0),
            ) // Start of month in UTC
            const endDate = new Date(
                Date.UTC(endYear, endMonth + 1, 0, 23, 59, 59, 999),
            ) // End of month in UTC

            result = {
                from: startDate,
                to: endDate,
            }
        }
    }

    // Check for single day (e.g., "20241005" or "2024-10-05")
    // Must be 8 digits (YYYYMMDD) or have separators to avoid ambiguity with YYYYMM format
    const singleDayMatch = cleanInput.match(
        /^(\d{4})([.-])(\d{1,2})\2(\d{1,2})$|^(\d{8})$/,
    )
    if (singleDayMatch) {
        let year: number
        let month: number
        let day: number

        if (singleDayMatch[5]) {
            // 8-digit format (YYYYMMDD)
            const fullMatch = singleDayMatch[5]
            year = parseInt(fullMatch.substring(0, 4))
            month = parseInt(fullMatch.substring(4, 6)) - 1 // JS months are 0-indexed
            day = parseInt(fullMatch.substring(6, 8))
        } else {
            // Separated format (YYYY-MM-DD or YYYY.MM.DD)
            year = parseInt(singleDayMatch[1])
            month = parseInt(singleDayMatch[3]) - 1 // JS months are 0-indexed
            day = parseInt(singleDayMatch[4])
        }

        if (
            year >= 2020 &&
            year <= new Date().getFullYear() &&
            month >= 0 &&
            month <= 11 &&
            day >= 1 &&
            day <= 31
        ) {
            const date = new Date(Date.UTC(year, month, day, 0, 0, 0, 0))
            // Validate the date (e.g., Feb 30 would be invalid)
            if (
                date.getUTCFullYear() === year &&
                date.getUTCMonth() === month &&
                date.getUTCDate() === day
            ) {
                if (singleDate) {
                    return date
                }
                result = {
                    from: date,
                    to: new Date(Date.UTC(year, month, day, 23, 59, 59, 999)),
                }
            }
        }
    }

    // Check for single year-month (e.g., "202210" or "2022.10")
    // Must be 6 digits (YYYYMM) or have a separator to avoid ambiguity
    const singleMonthMatch = cleanInput.match(
        /^(\d{4})[.-](\d{1,2})$|^(\d{6})$/,
    )
    if (singleMonthMatch && !result) {
        let year: number
        let month: number

        if (singleMonthMatch[3]) {
            // 6-digit format (YYYYMM)
            const fullMatch = singleMonthMatch[3]
            year = parseInt(fullMatch.substring(0, 4))
            month = parseInt(fullMatch.substring(4, 6)) - 1 // JS months are 0-indexed
        } else {
            // Separated format (YYYY-MM or YYYY.MM)
            year = parseInt(singleMonthMatch[1])
            month = parseInt(singleMonthMatch[2]) - 1 // JS months are 0-indexed
        }

        if (
            year >= 2020 &&
            year <= new Date().getFullYear() &&
            month >= 0 &&
            month <= 11
        ) {
            if (singleDate) {
                // In single date mode, return the first day of the month
                return new Date(Date.UTC(year, month, 1, 0, 0, 0, 0))
            }
            result = {
                from: new Date(Date.UTC(year, month, 1, 0, 0, 0, 0)), // Start of month in UTC
                to: new Date(Date.UTC(year, month + 1, 0, 23, 59, 59, 999)), // End of month in UTC
            }
        }
    }

    // Check for single year (e.g., "2022")
    const singleYearMatch = cleanInput.match(/^(\d{4})$/)
    if (singleYearMatch && !result) {
        const year = parseInt(singleYearMatch[1])

        if (year >= 2020 && year <= new Date().getFullYear()) {
            if (singleDate) {
                // In single date mode, return the first day of the year
                return new Date(Date.UTC(year, 0, 1, 0, 0, 0, 0))
            }
            result = {
                from: new Date(Date.UTC(year, 0, 1, 0, 0, 0, 0)), // Start of year in UTC
                to: new Date(Date.UTC(year, 11, 31, 23, 59, 59, 999)), // End of year in UTC
            }
        }
    }

    // Try to parse as ISO date strings (fallback)
    if (!result) {
        try {
            const isoMatch = cleanInput.match(
                /^(\d{4}-\d{2}-\d{2})(?:-(\d{4}-\d{2}-\d{2}))?$/,
            )
            if (isoMatch) {
                // Parse ISO dates and convert to UTC midnight/end-of-day
                const fromDateStr = isoMatch[1]
                const toDateStr = isoMatch[2] || isoMatch[1]

                const [fromYear, fromMonth, fromDay] = fromDateStr
                    .split('-')
                    .map(Number)
                const [toYear, toMonth, toDay] = toDateStr
                    .split('-')
                    .map(Number)

                const fromDate = new Date(
                    Date.UTC(fromYear, fromMonth - 1, fromDay, 0, 0, 0, 0),
                )
                const toDate = new Date(
                    Date.UTC(toYear, toMonth - 1, toDay, 23, 59, 59, 999),
                )

                if (
                    fromDate >= new Date(Date.UTC(2020, 0, 1)) &&
                    toDate <= new Date()
                ) {
                    if (singleDate && fromDateStr === toDateStr) {
                        // Single date in ISO format
                        return fromDate
                    }
                    result = {
                        from: fromDate,
                        to: toDate,
                    }
                }
            }
        } catch (error) {
            // Ignore parsing errors
        }
    }

    return result
}
/**
 * Formats a date range or single date to a string representation suitable for the input field.
 * Detects full years, months, or specific days and formats accordingly.
 */
function formatRange(range: DateRange | Date | undefined): string {
    if (range == null) {
        return ''
    }

    // Handle single Date
    if (range instanceof Date) {
        const formatYYYYMMDD = (d: Date): string =>
            `${d.getUTCFullYear()}${String(d.getUTCMonth() + 1).padStart(2, '0')}${String(d.getUTCDate()).padStart(2, '0')}`
        const formatYYYYMM = (d: Date): string =>
            `${d.getUTCFullYear()}${String(d.getUTCMonth() + 1).padStart(2, '0')}`
        const formatYYYY = (d: Date): string => `${d.getUTCFullYear()}`
        const formatISO = (d: Date): string => {
            const year = d.getUTCFullYear()
            const month = String(d.getUTCMonth() + 1).padStart(2, '0')
            const day = String(d.getUTCDate()).padStart(2, '0')
            return `${year}-${month}-${day}`
        }

        const isStartOfDay = (date: Date): boolean =>
            date.getUTCHours() === 0 &&
            date.getUTCMinutes() === 0 &&
            date.getUTCSeconds() === 0

        if (!isStartOfDay(range)) {
            return formatISO(range)
        }

        const isFullYearStart =
            range.getUTCMonth() === 0 && range.getUTCDate() === 1
        if (isFullYearStart) {
            return formatYYYY(range)
        }

        const isFullMonthStart = range.getUTCDate() === 1
        if (isFullMonthStart) {
            return formatYYYYMM(range)
        }

        return formatYYYYMMDD(range)
    }

    // Handle DateRange
    if (range.from == null) {
        return ''
    }

    if (!(range.from instanceof Date)) {
        range.from = new Date(range.from)
    }
    if (!(range.to instanceof Date)) {
        range.to = new Date(range.to ?? 0)
    }

    const { from, to } = range

    const formatYYYYMMDD = (d: Date): string =>
        `${d.getUTCFullYear()}${String(d.getUTCMonth() + 1).padStart(2, '0')}${String(d.getUTCDate()).padStart(2, '0')}`

    const formatYYYYMM = (d: Date): string =>
        `${d.getUTCFullYear()}${String(d.getUTCMonth() + 1).padStart(2, '0')}`

    const formatYYYY = (d: Date): string => `${d.getUTCFullYear()}`

    const formatISO = (d: Date): string => {
        const year = d.getUTCFullYear()
        const month = String(d.getUTCMonth() + 1).padStart(2, '0')
        const day = String(d.getUTCDate()).padStart(2, '0')
        return `${year}-${month}-${day}`
    }

    // Helper to check if a date is at start of day (00:00:00 UTC)
    const isStartOfDay = (date: Date): boolean =>
        date.getUTCHours() === 0 &&
        date.getUTCMinutes() === 0 &&
        date.getUTCSeconds() === 0

    // Helper to check if a date is at end of day (23:59:59 UTC)
    const isEndOfDay = (date: Date): boolean =>
        date.getUTCHours() === 23 &&
        date.getUTCMinutes() === 59 &&
        date.getUTCSeconds() === 59

    // Helper for end of month (UTC)
    const endOfMonthDate = (date: Date): Date => {
        return new Date(
            Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + 1, 0),
        )
    }

    if (to instanceof Date) {
        // Check for same day (full day scenario)
        if (
            from.getUTCFullYear() === to.getUTCFullYear() &&
            from.getUTCMonth() === to.getUTCMonth() &&
            from.getUTCDate() === to.getUTCDate() &&
            isStartOfDay(from) &&
            isEndOfDay(to)
        ) {
            return formatYYYYMMDD(from)
        }

        // Full year scenario
        const isFullYearStart =
            from.getUTCMonth() === 0 &&
            from.getUTCDate() === 1 &&
            isStartOfDay(from)
        const isFullYearEnd =
            to.getUTCMonth() === 11 && to.getUTCDate() === 31 && isEndOfDay(to)

        if (
            isFullYearStart &&
            isFullYearEnd &&
            from.getUTCFullYear() === to.getUTCFullYear()
        ) {
            return formatYYYY(from)
        }

        // Full month scenario (same month/year)
        const isFullMonthStart = from.getUTCDate() === 1 && isStartOfDay(from)
        const isFullMonthEnd =
            endOfMonthDate(to).getUTCDate() === to.getUTCDate() &&
            isEndOfDay(to)
        if (
            isFullMonthStart &&
            isFullMonthEnd &&
            from.getUTCMonth() === to.getUTCMonth() &&
            from.getUTCFullYear() === to.getUTCFullYear()
        ) {
            return formatYYYYMM(from)
        }

        // Full months across different months (same year)
        if (
            isFullMonthStart &&
            isFullMonthEnd &&
            from.getUTCFullYear() === to.getUTCFullYear()
        ) {
            return `${formatYYYYMM(from)}-${formatYYYYMM(to)}`
        }

        // Full years across different years
        if (isFullYearStart && isFullYearEnd) {
            return `${formatYYYY(from)}-${formatYYYY(to)}`
        }

        // Fallback: Specific day range (YYYYMMDD-YYYYMMDD)
        return `${formatYYYYMMDD(from)}-${formatYYYYMMDD(to)}`
    } else {
        // Single date (no 'to')
        if (isStartOfDay(from)) {
            // Assume single full day if at start; adjust if you want end-of-day check (but 'to' is undefined)
            const isFullYearStart =
                from.getUTCMonth() === 0 && from.getUTCDate() === 1
            if (isFullYearStart) {
                return formatYYYY(from)
            }
            const isFullMonthStart = from.getUTCDate() === 1
            if (isFullMonthStart) {
                return formatYYYYMM(from)
            }
        }
        return formatISO(from)
    }
}
